/**
 * The only colours components are allowed to reference.
 *
 * Light and dark are designed separately. Light is cream and sand with espresso
 * text; dark is warm charcoal with an espresso undertone and ivory text — not an
 * inversion, and never blue-black.
 *
 * Surface order, chrome outward to content:
 *   light — page cream, sidebar a deeper sand, cards warm near-white
 *   dark  — sidebar below the page, cards raised above it
 */
const pair = (base, dark) => ({ value: { base, _dark: dark } })

export const semanticTokens = {
  colors: {
    /* --------------------------------------------------------------- page --- */
    page: {
      bg: pair('{colors.warm.75}', '{colors.warm.950}'),
      bgSubtle: pair('{colors.warm.100}', '{colors.warm.925}'),
    },

    /* ----------------------------------------------------------- surfaces --- */
    surface: {
      default: pair('{colors.warm.50}', '{colors.warm.900}'),
      subtle: pair('{colors.warm.100}', '{colors.warm.925}'),
      raised: pair('{colors.warm.0}', '{colors.warm.850}'),
      sunken: pair('{colors.warm.100}', '{colors.warm.975}'),
      interactive: pair('{colors.warm.0}', '{colors.warm.850}'),
      hover: pair('{colors.warm.150}', '{colors.warm.850}'),
      // Restrained amber tint for hovering a branded or selectable row.
      hoverBrand: pair('{colors.orange.50}', '{colors.warm.850}'),
      // Pressed. Distinct from `selected`, which persists.
      active: pair('{colors.warm.200}', '{colors.warm.800}'),
      // Amber-tinted so selection reads as brand, not as grey.
      selected: pair('{colors.orange.50}', '{colors.orange.950}'),
      disabled: pair('{colors.warm.100}', '{colors.warm.850}'),
      inverted: pair('{colors.warm.1000}', '{colors.warm.50}'),
    },

    /* ------------------------------------------------------------- chrome --- */
    sidebar: {
      bg: pair('{colors.warm.100}', '{colors.warm.975}'),
      border: pair('{colors.warm.200}', '{colors.warm.900}'),
    },
    topbar: {
      // Brightest surface in light mode: the topbar reads as chrome above the
      // page, not as another card.
      bg: pair('{colors.warm.0}', '{colors.warm.925}'),
    },
    card: {
      bg: pair('{colors.warm.50}', '{colors.warm.900}'),
      border: pair('{colors.warm.200}', '{colors.warm.850}'),
    },

    /* ------------------------------------------------------------- inputs --- */
    input: {
      bg: pair('{colors.warm.0}', '{colors.warm.850}'),
      border: pair('{colors.warm.250}', '{colors.warm.750}'),
      hoverBorder: pair('{colors.warm.300}', '{colors.warm.700}'),
      // Matches focus.ring, for the same contrast reason.
      focusBorder: pair('{colors.orange.600}', '{colors.orange.400}'),
    },

    /* --------------------------------------------------------------- text --- */
    text: {
      primary: pair('{colors.warm.1000}', '{colors.warm.50}'),
      secondary: pair('{colors.warm.600}', '{colors.warm.400}'),
      // Dark mode needs a lighter step: warm.500 on a raised dark card is 3.6:1.
      muted: pair('{colors.warm.500}', '{colors.warm.450}'),
      disabled: pair('{colors.warm.400}', '{colors.warm.700}'),
      // On a brand or status fill.
      onSolid: pair('{colors.warm.0}', '{colors.warm.0}'),
      inverted: pair('{colors.warm.50}', '{colors.warm.1000}'),
    },

    /* -------------------------------------------------------------- icons --- */
    icon: {
      primary: pair('{colors.warm.600}', '{colors.warm.400}'),
      muted: pair('{colors.warm.400}', '{colors.warm.500}'),
      strong: pair('{colors.warm.800}', '{colors.warm.100}'),
    },

    /* ------------------------------------------------------------ borders --- */
    border: {
      subtle: pair('{colors.warm.150}', '{colors.warm.900}'),
      default: pair('{colors.warm.200}', '{colors.warm.850}'),
      strong: pair('{colors.warm.300}', '{colors.warm.750}'),
      // Hovered or otherwise live control edge.
      interactive: pair('{colors.warm.300}', '{colors.warm.700}'),
      // Focused or selected control edge. Amber, matching focus.ring.
      focus: pair('{colors.orange.600}', '{colors.orange.400}'),
    },

    /* -------------------------------------------------------------- brand --- */
    /**
     * Amber carries dark text in both modes — the same relationship as the
     * client site's hiring bar, and 7.6:1 either way. `fg` is the ochre/amber
     * used for brand-coloured text and icons, where a fill would be too loud.
     */
    brand: {
      solid: pair('{colors.orange.400}', '{colors.orange.400}'),
      hover: pair('{colors.orange.500}', '{colors.orange.300}'),
      active: pair('{colors.orange.600}', '{colors.orange.200}'),
      subtle: pair('{colors.orange.50}', '{colors.orange.950}'),
      muted: pair('{colors.orange.100}', '{colors.orange.900}'),
      border: pair('{colors.orange.200}', '{colors.orange.800}'),
      contrast: pair('{colors.warm.1000}', '{colors.warm.950}'),
      fg: pair('{colors.orange.700}', '{colors.orange.300}'),
    },

    /* ------------------------------------------------------------ accents --- */
    /** The three supporting logo colours. Used only where they mean something. */
    accent: {
      coral: pair('{colors.coral.600}', '{colors.coral.400}'),
      coralSubtle: pair('{colors.coral.50}', '{colors.coral.950}'),
      coralBorder: pair('{colors.coral.200}', '{colors.coral.800}'),
      teal: pair('{colors.teal.600}', '{colors.teal.400}'),
      tealSubtle: pair('{colors.teal.50}', '{colors.teal.950}'),
      tealBorder: pair('{colors.teal.200}', '{colors.teal.800}'),
      blue: pair('{colors.blue.600}', '{colors.blue.400}'),
      blueSubtle: pair('{colors.blue.50}', '{colors.blue.950}'),
      blueBorder: pair('{colors.blue.200}', '{colors.blue.800}'),
    },

    /* ------------------------------------------------------------- status --- */
    /**
     * Status maps onto the logo colours: teal succeeds, orange waits, coral
     * fails, blue informs. `*Fg` is the text colour for the matching `*Subtle`
     * background.
     */
    status: {
      success: pair('{colors.teal.600}', '{colors.teal.400}'),
      successSubtle: pair('{colors.teal.50}', '{colors.teal.950}'),
      successBorder: pair('{colors.teal.200}', '{colors.teal.800}'),
      successFg: pair('{colors.teal.700}', '{colors.teal.300}'),

      warning: pair('{colors.orange.500}', '{colors.orange.400}'),
      warningSubtle: pair('{colors.orange.50}', '{colors.orange.950}'),
      warningBorder: pair('{colors.orange.200}', '{colors.orange.800}'),
      warningFg: pair('{colors.orange.700}', '{colors.orange.300}'),

      error: pair('{colors.coral.600}', '{colors.coral.500}'),
      errorSubtle: pair('{colors.coral.50}', '{colors.coral.950}'),
      errorBorder: pair('{colors.coral.200}', '{colors.coral.800}'),
      errorFg: pair('{colors.coral.700}', '{colors.coral.300}'),

      info: pair('{colors.blue.600}', '{colors.blue.400}'),
      infoSubtle: pair('{colors.blue.50}', '{colors.blue.950}'),
      infoBorder: pair('{colors.blue.200}', '{colors.blue.800}'),
      infoFg: pair('{colors.blue.700}', '{colors.blue.300}'),

      neutral: pair('{colors.warm.500}', '{colors.warm.400}'),
      neutralSubtle: pair('{colors.warm.100}', '{colors.warm.850}'),
      neutralBorder: pair('{colors.warm.200}', '{colors.warm.750}'),
      neutralFg: pair('{colors.warm.600}', '{colors.warm.300}'),
    },

    /* -------------------------------------------------------------- focus --- */
    focus: {
      // Ochre in light mode: amber 400 only reaches 2:1 on cream, well under the
      // 3:1 a focus indicator needs. Dark mode keeps the brighter amber.
      ring: pair('{colors.orange.600}', '{colors.orange.400}'),
    },

    overlay: {
      bg: pair('{colors.scrim.light}', '{colors.scrim.dark}'),
    },

    /* -------------------------------------------------------------- chart --- */
    /** Orange leads, teal supports, blue is tertiary, coral flags problems. */
    chart: {
      // Ochre in light mode so the primary series clears 3:1 on a warm card.
      1: pair('{colors.orange.600}', '{colors.orange.400}'),
      2: pair('{colors.teal.500}', '{colors.teal.300}'),
      3: pair('{colors.blue.500}', '{colors.blue.300}'),
      4: pair('{colors.coral.500}', '{colors.coral.300}'),
      grid: pair('{colors.warm.200}', '{colors.warm.850}'),
      axis: pair('{colors.warm.500}', '{colors.warm.500}'),
    },
  },

  /** Elevation swaps with colour mode: dark surfaces need deeper shadows. */
  shadows: {
    xs: pair('{shadows.xsLight}', '{shadows.xsDark}'),
    sticky: pair('{shadows.stickyLight}', '{shadows.stickyDark}'),
    sm: pair('{shadows.smLight}', '{shadows.smDark}'),
    md: pair('{shadows.mdLight}', '{shadows.mdDark}'),
    lg: pair('{shadows.lgLight}', '{shadows.lgDark}'),
  },
}
